import { z } from 'zod';
import { canonicalizeCidr, parseCidr, parseIp, type IpCidr } from './ip';

/**
 * Routing domain constants, validation, and the desired-state shape for FRR
 * configuration (see ROUTING_ARCHITECTURE.md).
 *
 * The controller computes intended routing and renders deterministic FRR config
 * per device; the edge applies it under commit-confirm. Everything here is about
 * making an unsafe plan impossible to express or, failing that, impossible to
 * deploy.
 */

/** The backbone area every OSPF domain is anchored on. */
export const OSPF_BACKBONE_AREA = '0.0.0.0';

/** Private-use ASN ranges (RFC 6996). Public ASNs are allowed but flagged. */
export const ASN_PRIVATE_16_MIN = 64512;
export const ASN_PRIVATE_16_MAX = 65534;
export const ASN_PRIVATE_32_MIN = 4200000000;
export const ASN_PRIVATE_32_MAX = 4294967294;

export const ASN_MIN = 1;
export const ASN_MAX = 4294967295;

/** FRR's defaults; deviating from them is allowed but must be deliberate. */
export const OSPF_HELLO_DEFAULT = 10;
export const OSPF_DEAD_DEFAULT = 40;
export const OSPF_COST_MIN = 1;
export const OSPF_COST_MAX = 65535;

/** Default cap on prefixes accepted from a BGP neighbour, to contain leaks. */
export const BGP_MAX_PREFIX_DEFAULT = 1000;

export function isValidAsn(asn: number): boolean {
  return Number.isInteger(asn) && asn >= ASN_MIN && asn <= ASN_MAX;
}

export function isPrivateAsn(asn: number): boolean {
  return (
    (asn >= ASN_PRIVATE_16_MIN && asn <= ASN_PRIVATE_16_MAX) ||
    (asn >= ASN_PRIVATE_32_MIN && asn <= ASN_PRIVATE_32_MAX)
  );
}

/**
 * A router ID is a 32-bit value written as a dotted quad. It is not an address —
 * it only needs to be unique within the routing domain — but FRR requires the
 * dotted-quad form and rejects 0.0.0.0.
 */
export function isValidRouterId(routerId: string): boolean {
  const addr = parseIp(routerId);
  return addr !== null && addr.family === 4 && addr.value !== 0n;
}

/** OSPF area IDs share the router-ID format, and 0.0.0.0 *is* legal (backbone). */
export function isValidAreaId(areaId: string): boolean {
  const addr = parseIp(areaId);
  return addr !== null && addr.family === 4;
}

/** Interface network types FRR accepts for OSPF. */
export const OSPF_NETWORK_TYPES = [
  'point-to-point',
  'broadcast',
  'non-broadcast',
  'point-to-multipoint',
] as const;
export type OspfNetworkType = (typeof OSPF_NETWORK_TYPES)[number];

export const OSPF_AREA_TYPES = [
  'STANDARD',
  'STUB',
  'TOTALLY_STUB',
  'NSSA',
  'NSSA_TOTALLY_STUB',
] as const;
export type OspfAreaType = (typeof OSPF_AREA_TYPES)[number];

/** Values match the Prisma `BgpPeerType` enum so the two are interchangeable. */
export const BGP_PEER_TYPES = ['IBGP', 'EBGP'] as const;
export type BgpPeerType = (typeof BGP_PEER_TYPES)[number];

/** Kinds of generated routing policy, matching what the renderer emits. */
export const ROUTE_POLICY_KINDS = ['prefix-list', 'route-map'] as const;
export type RoutePolicyKind = (typeof ROUTE_POLICY_KINDS)[number];

/**
 * A BGP community in `asn:value` form, or a well-known name. Validated because a
 * malformed community silently changes which policy matches.
 */
export function isValidCommunity(community: string): boolean {
  if (['no-export', 'no-advertise', 'local-AS', 'internet'].includes(community)) return true;
  const parts = community.split(':');
  if (parts.length !== 2) return false;
  return parts.every((p) => /^\d{1,5}$/.test(p) && Number(p) <= 65535);
}

// ---------------------------------------------------------------------------
// Route-safety primitives
// ---------------------------------------------------------------------------

/** Stable identifiers for the safety checks, used as finding `kind` values. */
export const RouteFindingKind = {
  PREFIX_OVERLAP: 'prefix-overlap',
  DUPLICATE_TUNNEL_PREFIX: 'duplicate-tunnel-prefix',
  DEFAULT_ROUTE_CONFLICT: 'default-route-conflict',
  WAN_SUBNET_ADVERTISED: 'wan-subnet-advertised',
  MANAGEMENT_PATH_LOSS: 'management-path-loss',
  UNFILTERED_REDISTRIBUTION: 'unfiltered-redistribution',
  MISSING_MAX_PREFIX: 'missing-max-prefix',
  DUPLICATE_ROUTER_ID: 'duplicate-router-id',
  ASN_CONFLICT: 'asn-conflict',
  EXCESSIVE_ROUTE_GROWTH: 'excessive-route-growth',
  ROUTE_FLAPPING: 'route-flapping',
  RECURSIVE_NEXT_HOP: 'recursive-next-hop',
  AREA_WITHOUT_BACKBONE: 'area-without-backbone',
} as const;
export type RouteFindingKind = (typeof RouteFindingKind)[keyof typeof RouteFindingKind];

/**
 * Prefixes that must never be advertised into the overlay. Loopback, link-local,
 * and multicast ranges in a routing table indicate a misconfiguration, and
 * advertising them can black-hole local traffic on every receiving device.
 */
export const NEVER_ADVERTISE_V4 = ['127.0.0.0/8', '169.254.0.0/16', '224.0.0.0/4'] as const;
export const NEVER_ADVERTISE_V6 = ['::1/128', 'fe80::/10', 'ff00::/8'] as const;

/** True when the prefix is one of the ranges that must never be advertised. */
export function isNeverAdvertised(prefix: string): boolean {
  const cidr = parseCidr(prefix);
  if (!cidr) return false;
  const reserved = cidr.family === 4 ? NEVER_ADVERTISE_V4 : NEVER_ADVERTISE_V6;
  return reserved.some((r) => {
    const range = parseCidr(r);
    return range !== null && contains(range, cidr);
  });
}

function contains(outer: IpCidr, inner: IpCidr): boolean {
  if (outer.family !== inner.family) return false;
  if (inner.prefix < outer.prefix) return false;
  const bits = outer.family === 4 ? 32 : 128;
  const mask =
    outer.prefix === 0 ? 0n : ((1n << BigInt(outer.prefix)) - 1n) << BigInt(bits - outer.prefix);
  return (inner.base & mask) === outer.base;
}

/** True for `0.0.0.0/0` or `::/0`. */
export function isDefaultPrefix(prefix: string): boolean {
  const cidr = parseCidr(prefix);
  return cidr !== null && cidr.prefix === 0;
}

// ---------------------------------------------------------------------------
// Desired-state schema for the `frr` resource
// ---------------------------------------------------------------------------

const cidrSchema = z.string().refine((v) => parseCidr(v) !== null, 'must be a valid CIDR');

export const ospfInterfaceSpecSchema = z.object({
  name: z.string().min(1).max(15),
  area: z.string().refine(isValidAreaId, 'must be a dotted-quad area id'),
  networkType: z.enum(OSPF_NETWORK_TYPES).default('point-to-point'),
  cost: z.number().int().min(OSPF_COST_MIN).max(OSPF_COST_MAX).optional(),
  helloInterval: z.number().int().min(1).max(65535).optional(),
  deadInterval: z.number().int().min(1).max(65535).optional(),
  passive: z.boolean().default(false),
  bfd: z.boolean().default(false),
  /** Present only when authentication is configured; the key itself is separate. */
  authenticated: z.boolean().default(false),
});
export type OspfInterfaceSpec = z.infer<typeof ospfInterfaceSpecSchema>;

export const ospfAreaSpecSchema = z.object({
  area: z.string().refine(isValidAreaId, 'must be a dotted-quad area id'),
  type: z.enum(OSPF_AREA_TYPES).default('STANDARD'),
  ranges: z.array(cidrSchema).default([]),
  defaultOriginate: z.boolean().default(false),
});
export type OspfAreaSpec = z.infer<typeof ospfAreaSpecSchema>;

export const bgpNeighborSpecSchema = z.object({
  address: z.string().refine((v) => parseIp(v) !== null, 'must be an IP address'),
  remoteAsn: z.number().int().refine(isValidAsn, 'must be a valid ASN'),
  peerType: z.enum(BGP_PEER_TYPES),
  routeReflectorClient: z.boolean().default(false),
  /** Required in practice: an unbounded session can absorb a full table. */
  maxPrefix: z.number().int().positive().optional(),
  localPref: z.number().int().min(0).optional(),
  med: z.number().int().min(0).optional(),
  asPathPrepend: z.number().int().min(0).max(10).default(0),
  communities: z.array(z.string().refine(isValidCommunity, 'invalid community')).default([]),
  inboundPolicy: z.string().optional(),
  outboundPolicy: z.string().optional(),
  gracefulRestart: z.boolean().default(true),
  bfd: z.boolean().default(false),
  authenticated: z.boolean().default(false),
});
export type BgpNeighborSpec = z.infer<typeof bgpNeighborSpecSchema>;

export const routePolicyEntrySchema = z.object({
  seq: z.number().int().positive(),
  action: z.enum(['permit', 'deny']),
  prefix: cidrSchema.optional(),
  le: z.number().int().min(0).max(128).optional(),
  ge: z.number().int().min(0).max(128).optional(),
  match: z.record(z.string()).optional(),
  set: z.record(z.union([z.string(), z.number()])).optional(),
});

export const routePolicySpecSchema = z.object({
  name: z.string().min(1).max(64),
  kind: z.enum(ROUTE_POLICY_KINDS),
  entries: z.array(routePolicyEntrySchema).min(1),
});
export type RoutePolicySpec = z.infer<typeof routePolicySpecSchema>;

/** The `frr` resource applied by the edge. */
export const frrSpecSchema = z.object({
  routerId: z.string().refine(isValidRouterId, 'must be a non-zero dotted-quad router id'),
  localAsn: z.number().int().refine(isValidAsn, 'must be a valid ASN').optional(),
  ospf: z
    .object({
      enabled: z.boolean().default(false),
      areas: z.array(ospfAreaSpecSchema).default([]),
      interfaces: z.array(ospfInterfaceSpecSchema).default([]),
      redistribute: z.array(z.string()).default([]),
    })
    .optional(),
  ospf6: z
    .object({
      enabled: z.boolean().default(false),
      areas: z.array(ospfAreaSpecSchema).default([]),
      interfaces: z.array(ospfInterfaceSpecSchema).default([]),
      redistribute: z.array(z.string()).default([]),
    })
    .optional(),
  bgp: z
    .object({
      enabled: z.boolean().default(false),
      neighbors: z.array(bgpNeighborSpecSchema).default([]),
      redistribute: z.array(z.string()).default([]),
      networks: z.array(cidrSchema).default([]),
    })
    .optional(),
  policies: z.array(routePolicySpecSchema).default([]),
  bfd: z.boolean().default(false),
});
export type FrrSpec = z.infer<typeof frrSpecSchema>;

/**
 * Semantic validation of an FRR spec, beyond what the schema can express.
 *
 * These are the errors that would produce a config FRR accepts but an operator
 * did not intend — the kind that black-holes a fleet rather than failing loudly.
 */
export function validateFrrSpec(spec: FrrSpec): string[] {
  const errors: string[] = [];

  const checkOspf = (
    label: string,
    ospf: { enabled: boolean; areas: OspfAreaSpec[]; interfaces: OspfInterfaceSpec[] } | undefined,
  ): void => {
    if (!ospf?.enabled) return;

    const areaIds = new Set(ospf.areas.map((a) => a.area));
    for (const iface of ospf.interfaces) {
      if (!areaIds.has(iface.area)) {
        errors.push(`${label}: interface ${iface.name} references undeclared area ${iface.area}`);
      }
      // A dead interval at or below the hello interval tears the adjacency down
      // as fast as it forms, producing a permanently flapping neighbour.
      const hello = iface.helloInterval ?? OSPF_HELLO_DEFAULT;
      const dead = iface.deadInterval ?? OSPF_DEAD_DEFAULT;
      if (dead <= hello) {
        errors.push(
          `${label}: interface ${iface.name} has dead interval ${dead} <= hello ${hello}; ` +
            'the adjacency would never stay up',
        );
      }
    }

    // An area other than the backbone needs the backbone present somewhere in the
    // domain, or its inter-area routes have nowhere to go.
    const nonBackbone = ospf.areas.filter((a) => a.area !== OSPF_BACKBONE_AREA);
    if (nonBackbone.length > 0 && !areaIds.has(OSPF_BACKBONE_AREA)) {
      errors.push(
        `${label}: areas ${nonBackbone.map((a) => a.area).join(', ')} declared without the ` +
          `backbone area ${OSPF_BACKBONE_AREA} on this device`,
      );
    }

    for (const area of ospf.areas) {
      for (const range of area.ranges) {
        if (canonicalizeCidr(range) !== range.trim()) {
          errors.push(`${label}: area ${area.area} range "${range}" has host bits set`);
        }
      }
      // A stub area cannot carry external LSAs, so originating a default into it
      // is the only way out — but a totally-stub area already gets one implicitly.
      if (area.type === 'TOTALLY_STUB' && area.defaultOriginate) {
        errors.push(
          `${label}: area ${area.area} is totally-stub and already receives a default route`,
        );
      }
    }
  };

  checkOspf('ospf', spec.ospf);
  checkOspf('ospf6', spec.ospf6);

  if (spec.bgp?.enabled) {
    if (spec.localAsn === undefined) {
      errors.push('bgp: enabled without a local ASN');
    }
    const seen = new Set<string>();
    for (const neighbor of spec.bgp.neighbors) {
      if (seen.has(neighbor.address)) {
        errors.push(`bgp: duplicate neighbour ${neighbor.address}`);
      }
      seen.add(neighbor.address);

      // iBGP means "same AS" by definition; getting this wrong changes how routes
      // propagate and whether the AS-path loop check applies.
      if (spec.localAsn !== undefined) {
        if (neighbor.peerType === 'IBGP' && neighbor.remoteAsn !== spec.localAsn) {
          errors.push(
            `bgp: neighbour ${neighbor.address} is marked iBGP but its ASN ` +
              `${neighbor.remoteAsn} differs from the local ASN ${spec.localAsn}`,
          );
        }
        if (neighbor.peerType === 'EBGP' && neighbor.remoteAsn === spec.localAsn) {
          errors.push(
            `bgp: neighbour ${neighbor.address} is marked eBGP but shares the local ASN ` +
              `${spec.localAsn}`,
          );
        }
      }

      // An unbounded eBGP session can accept a full table and exhaust the edge.
      if (neighbor.peerType === 'EBGP' && neighbor.maxPrefix === undefined) {
        errors.push(
          `bgp: eBGP neighbour ${neighbor.address} has no maximum-prefix limit; ` +
            'an unbounded session can absorb a full table',
        );
      }
    }

    for (const network of spec.bgp.networks) {
      if (isDefaultPrefix(network)) {
        errors.push('bgp: refusing to originate a default route from the network statement');
      }
      if (isNeverAdvertised(network)) {
        errors.push(`bgp: network ${network} is in a range that must never be advertised`);
      }
    }
  }

  // Policy names referenced by neighbours must exist, or FRR silently applies no
  // filter — which is exactly the unfiltered case the guardrails forbid.
  const policyNames = new Set(spec.policies.map((p) => p.name));
  for (const neighbor of spec.bgp?.neighbors ?? []) {
    for (const [direction, name] of [
      ['inbound', neighbor.inboundPolicy],
      ['outbound', neighbor.outboundPolicy],
    ] as const) {
      if (name && !policyNames.has(name)) {
        errors.push(
          `bgp: neighbour ${neighbor.address} references ${direction} policy "${name}" ` +
            'which is not defined in this spec',
        );
      }
    }
  }

  for (const policy of spec.policies) {
    const seqs = new Set<number>();
    for (const entry of policy.entries) {
      if (seqs.has(entry.seq)) {
        errors.push(`policy ${policy.name}: duplicate sequence ${entry.seq}`);
      }
      seqs.add(entry.seq);
      if (entry.prefix && entry.le !== undefined && entry.ge !== undefined && entry.ge > entry.le) {
        errors.push(`policy ${policy.name}: seq ${entry.seq} has ge ${entry.ge} > le ${entry.le}`);
      }
    }
  }

  return errors;
}
